import React from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from '../../store/AuthContext';
import Dimension from "../../models/Dimension";
import PropsContainer from "../../components/props/PropsContainer"
import QRCodeBlock from "../../components/QRCodeBlock"
import DimensionIcon from "../../components/DimensionIcon"
import DimensionsService from "../../services/DimensionsService"
import AccountService from "../../services/AccountService";
import NativeInterface from "../../services/NativeInterface"
import SettingsService from "../../services/SettingsService"
import { Accordion, Form, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';

import {PiArrowSquareOut} from 'react-icons/pi';
import PermissionBlock from "../../components/PermissionBlock";

function updateVisibilityProp(props: any, enabled: boolean)
{
    if (!props[0] ||
        !props[0].items ||
        !props[0]._group ||
        !props[0]._group.title ||
        props[0]._group.title != 'General')
    {
        return;
    }
    for (let item of props[0].items) {
        if (item.title == 'Visibility') {
            item.readOnly = !enabled;
            break;
        }
    }
}

const EditDimensionMain: React.FC<{data: Dimension}> = (props) => {
    const authCtx = React.useContext(AuthContext);
    const navigate = useNavigate();
    const [dimensionEnv, setDimensionEnv] = React.useState<string>(getDimensionEnv(props.data.id));
    const [publishInProgress, setPublishInProgress] = React.useState(false)
 
    function getUpdatedProperties() {
        updateVisibilityProp(props.data.properties, authCtx.account.studio.canListPublic);
        return props.data.properties;
    }

    function getDimensionEnv(dimId: string) {
        return dimId in authCtx.account.dimensions ? authCtx.account.dimensions[dimId].env : '';
    }

    function envChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        authCtx.setDimensionEnv(props.data.id, event.target.value);
        setDimensionEnv(event.target.value);
    }

    function updateDimension(params: any) {
        return DimensionsService.updateDimension('dev', props.data.id, params);
    }

    async function deleteHandler() {
        if (!window.confirm(`Do you want to delete dimension '${props.data.name}'?`)) {
            return;
        }
        console.log(`Deleting dimension: ${props.data.id}`);
        await DimensionsService.deleteDimension(props.data.id);
        AccountService.reloadCurrentAccount()
        navigate(`/own-dimensions`);
    }

    function handleSceneEditor() {
        NativeInterface.launchNativeApp({dim: props.data.id, edit: 1});
    }

    function handleCodeEditor() {
        if (authCtx.account.studio.canEditCode) {
            const env = getDimensionEnv(props.data.id);
            window.open(`${SettingsService.codeEditorHost()}/?dim=${props.data.id!}&env=${env}`, '_blank');
        } else {
            alert("Your account is not enabled for code editing");
        }
    }

    function handlePublish() {
        if (!window.confirm(`Do you want to publish dimension '${props.data.name}'?`)) {
            return;
        }
        setPublishInProgress(true)
        DimensionsService.publishDimension(props.data.id)
        .then((response: any) => {
            setPublishInProgress(false)
            window.alert("Dimension published successfully!")
        })
        .catch((error: string) => {
            setPublishInProgress(false)
            window.alert(`Failed to publish: ${error}`)
        })
    }
   
    async function updateAccountData() {
        await AccountService.reloadCurrentAccount();
    }

    return (
        <>
            <div className="d-flex align-items-center" style={{marginBottom: "var(--margin-half)"}}>
                <div style={{width: "80px", height: "80px", marginRight: 10, flexShrink: 0}}>
                    <DimensionIcon dimId={props.data.id}/>
                </div>
                <h4>{props.data.name}</h4>
                <div className="d-flex flex-fill justify-content-end">
                    {!NativeInterface.isPWA() && (
                        <div className="large-screen-only">
                            <div className="d-flex flex-fill justify-content-end">
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        (!dimensionEnv || dimensionEnv === 'prod') ? (
                                            <Tooltip id="scene-editor-tooltip">
                                                Select a non-prod environment to edit
                                            </Tooltip>
                                        ) : <></>
                                    }
                                >
                                    <span>
                                        <Button
                                            className="me-3"
                                            variant="primary"
                                            onClick={handleSceneEditor}
                                            disabled={!dimensionEnv || dimensionEnv === 'prod'}
                                            style={{ pointerEvents: (!dimensionEnv || dimensionEnv === 'prod') ? 'auto' : undefined }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                Scene Editor
                                                <PiArrowSquareOut size={24} style={{ marginLeft: '8px' }} />
                                            </span>
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        (!dimensionEnv || dimensionEnv === 'prod') ? (
                                            <Tooltip id="code-editor-tooltip">
                                                Select a non-prod environment to edit
                                            </Tooltip>
                                        ) : <></>
                                    }
                                >
                                    <span>
                                        <Button
                                            className="me-3"
                                            variant="primary"
                                            onClick={handleCodeEditor}
                                            disabled={!dimensionEnv || dimensionEnv == 'prod'}
                                            style={{ pointerEvents: (!dimensionEnv || dimensionEnv === 'prod') ? 'auto' : undefined }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                Code Editor
                                                <PiArrowSquareOut size={24} style={{ marginLeft: '8px' }} />
                                            </span>
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            </div>
                        </div>
                    )}
                    { authCtx.account.isDimensionAdmin(props.data.id) &&
                        <Button style={{ width: "150px"}} variant="warning" onClick={handlePublish} disabled={publishInProgress}>
                            Publish
                        </Button>
                    }
                </div>
            </div>
            
            <PropsContainer properties={getUpdatedProperties()} doSave={updateDimension} />

            <Accordion>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Generate QR Code</Accordion.Header>
                    <Accordion.Body>
                        <QRCodeBlock dimId={props.data.id} locations={props.data.locations} />
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                    <Accordion.Header>Advanced</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group controlId="main-advanced-section-env" style={{ marginBottom: "var(--margin)"}}>
                            <Form.Label>Environment</Form.Label>
                            <Form.Control as="select" onChange={envChangeHandler} value={dimensionEnv}>
                                <option value=""></option>
                                <option value="prod">prod</option>
                                <option value="dev">dev</option>
                                <option value="sergei">sergei</option>
                                <option value="denis">denis</option>
                                <option value="alexey">alexey</option>
                            </Form.Control>
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>
                { authCtx.account.isDimensionAdmin(props.data.id) && (
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>User access</Accordion.Header>
                        <Accordion.Body>
                            <PermissionBlock 
                                dimension={props.data.id}
                                account={authCtx.account.id}
                                onPermissionsChange={updateAccountData}
                             />
                        </Accordion.Body>
                    </Accordion.Item>
                )}
                { authCtx.account.isDimensionAdmin(props.data.id) && (
                    <Accordion.Item eventKey="3">
                        <Accordion.Header>Delete Dimension</Accordion.Header>
                        <Accordion.Body>
                            <Button variant="danger" onClick={deleteHandler}>Delete Dimension</Button>
                        </Accordion.Body>
                    </Accordion.Item>
                )}
            </Accordion>
        </>
    );
};

export default EditDimensionMain;
